import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  ArrowLeft,
  Copy,
  Code,
  FileText,
  Globe,
  Users,
  CheckCircle
} from 'lucide-react';
import Button from '../../components/ui/button';
import Badge from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import axios from '../../utils/axios';
import toast from 'react-hot-toast';
import { formatDate } from '../../lib/utils';
import { useAuth } from '../../hooks/useAuth';

const TemplateView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState({});


  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const categoriesRes = await axios.get('/templates/meta/categories');

        setCategories(categoriesRes.data.data);

      } catch (error) {
        console.error('Failed to load metadata:', error);
      }
    };

    const loadTemplate = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/templates/${id}`);
        const loadedTemplate = response.data.data;

        // Ensure the template is public or owned by the current user
        if (
          loadedTemplate.visibility !== 'public' &&
          loadedTemplate.user_id !== currentUser?.id
        ) {
          toast.error('You do not have permission to view this template');
          navigate('/templates');
          return;
        }

        setTemplate(loadedTemplate);
      } catch (error) {
        console.error('Failed to load template:', error);
        toast.error('Failed to load template');
        navigate('/templates');
      } finally {
        setLoading(false);
      }
    };

    loadMetadata();
    loadTemplate();
  }, [id, currentUser, navigate]);

  const handleClone = async () => {
    try {
      const response = await axios.post(`/templates/${id}/clone`);
      toast.success('Template cloned successfully');
      navigate(`/templates/${response.data.data.id}`);
    } catch (error) {
      console.error('Failed to clone template:', error);
      toast.error('Failed to clone template');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'coding':
        return <Code className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <Globe className="w-4 h-4" />;
      case 'organization':
        return <Users className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading template...</p>
          </div>
        </div>
      </EvaluationLayout>
    );
  }

  if (!template) {
    return null;
  }

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/templates')}
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{template.name}</h1>
              <p className="text-muted-foreground mt-1">
                {template.description || 'No description provided'}
              </p>
            </div>
          </div>

          <div className="flex space-x-3">
            {template.user_id === currentUser?.id && (
              <Button
                variant="outline"
                onClick={() => navigate(`/templates/${id}/edit`)}
              >
                Edit
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleClone}
            >
              <Copy className="w-4 h-4 mr-2" />
              Clone Template
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Template Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Category</span>
                <div className="flex items-center space-x-2">
                  {getCategoryIcon(template.category)}
                  <span>{categories[template.category] || template.category}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Visibility</span>
                <div className="flex items-center space-x-2">
                  {getVisibilityIcon(template.visibility)}
                  <span>{template.visibility}</span>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last Updated</span>
                <span>{formatDate(template.updated_at)}</span>
              </div>

              {template.tags && template.tags.length > 0 && (
                <div>
                  <span className="text-muted-foreground block mb-2">Tags</span>
                  <div className="flex flex-wrap gap-2">
                    {template.tags.map((tag, index) => (
                      <Badge key={index} variant="outline">{tag}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Template Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Messages</h4>
                <div className="space-y-2">
                  {template.template_data.messages.map((message, index) => (
                    <div
                      key={index}
                      className="border border-border rounded-lg p-3 bg-secondary/50"
                    >
                      <Badge variant={message.role === 'system' ? 'default' : 'outline'} className="mb-2">
                        {message.role}
                      </Badge>
                      <p className="text-sm font-mono whitespace-pre-wrap">
                        {message.content || 'Empty message'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {template.template_data.prompt && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Evaluation Prompt</h4>
                  <div className="border border-border rounded-lg p-3 bg-secondary/50">
                    <p className="text-sm font-mono whitespace-pre-wrap">
                      {template.template_data.prompt}
                    </p>
                  </div>
                </div>
              )}

              {template.template_data.expected_output && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Expected Output</h4>
                  <div className="border border-border rounded-lg p-3 bg-secondary/50">
                    <p className="text-sm font-mono whitespace-pre-wrap">
                      {template.template_data.expected_output}
                    </p>
                  </div>
                </div>
              )}

              {template.template_data.evaluation_criteria && template.template_data.evaluation_criteria.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">Evaluation Criteria</h4>
                  <div className="space-y-2">
                    {template.template_data.evaluation_criteria.map((criterion, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-2 text-sm"
                      >
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span>{criterion}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Test Cases Card */}
          {template.template_data.test_cases && template.template_data.test_cases.length > 0 && (
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle>Test Cases</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {template.template_data.test_cases.map((testCase, index) => (
                    <div
                      key={index}
                      className="border border-border rounded-lg p-4 bg-secondary/30"
                    >
                      <h5 className="text-sm font-medium mb-3">Test Case {index + 1}</h5>
                      <div className="space-y-3">
                        <div>
                          <span className="text-xs font-medium text-muted-foreground">Input:</span>
                          <div className="mt-1 p-3 bg-background border border-border rounded text-sm font-mono whitespace-pre-wrap break-words">
                            {testCase.input || 'No input provided'}
                          </div>
                        </div>
                        <div>
                          <span className="text-xs font-medium text-muted-foreground">Expected Output:</span>
                          <div className="mt-1 p-3 bg-background border border-border rounded text-sm font-mono whitespace-pre-wrap break-words">
                            {testCase.expected || 'No expected output provided'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </EvaluationLayout>
  );
};

export default TemplateView;
